// Dragging a die out of the tray. From the first pixel the thing under the
// cursor is a real board die (createDiceMesh, lifted), so nothing swaps when
// it lands: on release it drops, the spawn intent carries its face, and the
// dice fold adopts that same mesh as the server's die (adoptTrayGhost). A
// release back over the tray puts it away.
import { DICE_CONFIGS, createDiceMesh, disposeDiceMesh, liftDie, ownDicePalette, setDieFaceUp, settleDie } from '../../../utils/game/diceMesh';
import { spawnDiceIntent } from '../../../utils/game/net/diceIntents';
import { playSound } from '../../../utils/game/sounds';

/** A spawn the server never answers (rejected, offline) is put away after this. */
const GHOST_TTL_MS = 5000;
const SAME_SPOT = 0.01;

export function beginDiceTrayDrag(host, dieType, event) {
  if (host._diceTrayDrag) return false;
  const point = host.scene?.raycastTablePoint(event);
  if (!point) return false;
  const faces = DICE_CONFIGS[dieType]?.faces || 6;
  const instance = {
    id: `tray-${Date.now()}`, dieType, x: point.x, z: point.z,
    value: 1 + Math.floor(Math.random() * faces), seat: host._localSeat(),
    palette: ownDicePalette(),
  };
  const mesh = createDiceMesh(instance);
  host.scene.scene.add(mesh);
  liftDie(mesh);
  setDieFaceUp(mesh, instance.value, host._diceContext());
  host._diceTrayDrag = { mesh };
  playSound('cardPickup');
  return true;
}

export function moveDiceTrayDrag(host, event) {
  const drag = host._diceTrayDrag;
  const point = drag && host.scene.raycastTablePoint(event);
  if (!point) return;
  drag.mesh.position.x = point.x;
  drag.mesh.position.z = point.z;
}

export function endDiceTrayDrag(host, event, { cancel = false } = {}) {
  const drag = host._diceTrayDrag;
  if (!drag) return;
  if (!cancel) moveDiceTrayDrag(host, event);
  host._diceTrayDrag = null;
  const { mesh } = drag;
  if (cancel) {
    disposeDiceMesh(mesh);
    return;
  }
  settleDie(mesh, host._diceContext());
  playSound('cardPlace');
  const inst = mesh.userData.diceInstance;
  host._sendMatchIntent(spawnDiceIntent({ dieType: inst.dieType, seat: inst.seat, x: inst.x, z: inst.z, value: inst.value, on: inst.on }));
  const ghosts = (host._diceTrayGhosts ??= []);
  ghosts.push(mesh);
  setTimeout(() => {
    const at = ghosts.indexOf(mesh);
    if (at < 0) return;
    ghosts.splice(at, 1);
    disposeDiceMesh(mesh);
  }, GHOST_TTL_MS);
}

/** The dropped mesh that `die` is the server's answer to, taken off the wait list. */
export function adoptTrayGhost(host, die) {
  const ghosts = host._diceTrayGhosts;
  if (!ghosts) return null;
  const at = ghosts.findIndex((mesh) => {
    const inst = mesh.userData.diceInstance;
    return inst.dieType === die.dieType && inst.seat === die.seat
      && Math.abs(inst.x - die.x) < SAME_SPOT && Math.abs(inst.z - die.z) < SAME_SPOT;
  });
  return at < 0 ? null : ghosts.splice(at, 1)[0];
}
